/** «Скрыть у себя» — ЛИЧНЫЙ фильтр обзора страницы распределения.
 *
 *  Строки скрытых каналов/регионов исчезают из таблицы у ОДНОГО человека в ОДНОМ браузере
 *  (`localStorage`, как выделение и видимость колонок). Это обычный фильтр: потребность,
 *  «Решения» и движок не меняются — скрытое они по-прежнему видят и считают.
 *
 *  ⚠️ А вот ФИКСАЦИЯ увозит РОВНО показанное: она собирает строки из того же дерева, что и
 *  таблица, и решения по скрытым строкам в поставки не попадают. Это не «недосмотр, который
 *  когда-нибудь починят», а свойство кнопки «что видно, то и уедет» — но человеку оно должно
 *  говориться вслух (`fixationScope` ниже), а не выясняться после клика.
 *
 *  Не путать с «Куда планируем» (`PUT /api/supply/targets`, таблица `supply_disabled_target`):
 *  она тоже личная (с 17.09.2026), но меняет РАСЧЁТ — выключенное не попадает в потребность и
 *  фиксацию этого пользователя. «Скрыть у себя» не трогает числа вовсе: это фильтр обзора, и
 *  живёт он в браузере, а не в БД.
 */

import { plural } from "./format";

/** Что человек спрятал у себя. Каналы и регионы — РАЗНЫЕ множества: сентинела «регион = канал
 *  целиком» здесь нет, декодировать нечего (у целей «Куда планируем» он есть и живёт на бэкенде). */
export interface HiddenByMe {
  /** Коды каналов, скрытых целиком (`wb` / `ozon` / `ym`). */
  readonly channels: ReadonlySet<string>;
  /** Ключи `канал|регион`: имя региона уникально только внутри своего канала. */
  readonly regions: ReadonlySet<string>;
}

/** Ничего не скрыто. Общая константа — чтобы «пусто» везде было одним значением. */
export const NOTHING_HIDDEN: HiddenByMe = {
  channels: new Set<string>(),
  regions: new Set<string>(),
};

/** Ключ скрытого региона. Собирается РОВНО здесь — разобрать его умеет `channelOfKey`. */
export const hiddenRegionKey = (channel: string, region: string): string =>
  `${channel}|${region}`;

const channelOfKey = (key: string): string => key.slice(0, key.indexOf("|"));

/** Скрыта ли цель лично у этого человека. Без `region` — вопрос про канал целиком.
 *  Скрытый канал накрывает все свои регионы: иначе каждый потребитель проверял бы два условия
 *  и однажды забыл бы одно. */
export function isHiddenByMe(
  hidden: HiddenByMe,
  channel: string,
  region?: string,
): boolean {
  if (hidden.channels.has(channel)) return true;
  return region !== undefined && hidden.regions.has(hiddenRegionKey(channel, region));
}

/** Сколько спрятано — для постоянного счётчика над таблицей. Регионы внутри скрытого целиком
 *  канала не считаем дважды: человек спрятал канал, а не двадцать строк. */
export function hiddenCounts(hidden: HiddenByMe): {
  channels: number;
  regions: number;
  total: number;
} {
  let regions = 0;
  for (const key of hidden.regions)
    if (!hidden.channels.has(channelOfKey(key))) regions += 1;
  return {
    channels: hidden.channels.size,
    regions,
    total: hidden.channels.size + regions,
  };
}

/** Скрытое словами: «2 региона · 1 канал». Одна фраза на баннер над таблицей, подсказку кнопки
 *  и подтверждение — иначе они разойдутся склонениями и порядком. Пусто — пустая строка. */
export function hiddenWords(hidden: HiddenByMe): string {
  const counts = hiddenCounts(hidden);
  return [
    counts.regions > 0 && plural(counts.regions, "регион", "региона", "регионов"),
    counts.channels > 0 && plural(counts.channels, "канал", "канала", "каналов"),
  ]
    .filter(Boolean)
    .join(" · ");
}

/** Регионы канала, спрятанные у себя. Нужен пикеру: показать в списке даже те, что выпали из
 *  отчётов, — иначе скрытое нечем вернуть, кроме «показать всё». */
export function hiddenRegionsOf(hidden: HiddenByMe, channel: string): string[] {
  const prefix = `${channel}|`;
  return [...hidden.regions]
    .filter((k) => k.startsWith(prefix))
    .map((k) => k.slice(prefix.length));
}

/** Спрятать/вернуть канал целиком. Отметки его регионов сохраняются: вернув канал, человек
 *  получает ровно тот обзор, что был до скрытия. */
export function setChannelHidden(
  hidden: HiddenByMe,
  channel: string,
  hide: boolean,
): HiddenByMe {
  const channels = new Set(hidden.channels);
  if (hide) channels.add(channel);
  else channels.delete(channel);
  return { channels, regions: hidden.regions };
}

/** Спрятать/вернуть регионы канала (одним вызовом — у Ozon кластеров под тридцать). */
export function setRegionsHidden(
  hidden: HiddenByMe,
  channel: string,
  regions: readonly string[],
  hide: boolean,
): HiddenByMe {
  const next = new Set(hidden.regions);
  for (const r of regions) {
    const key = hiddenRegionKey(channel, r);
    if (hide) next.add(key);
    else next.delete(key);
  }
  return { channels: hidden.channels, regions: next };
}

/** Канал с пережившими личное скрытие регионами. */
export interface VisibleChannel<Ch extends string, R> {
  channel: Ch;
  regions: R[];
}

/** Видимые каналы товара. Узел канала исчезает, если скрыты ВСЕ его регионы: пустая ветка,
 *  которая не раскрывается, читается как «данные потерялись». */
export function visibleChannels<Ch extends string, R extends { region: string }>(
  hidden: HiddenByMe,
  channels: readonly { channel: Ch; regions: readonly R[] }[],
): VisibleChannel<Ch, R>[] {
  const out: VisibleChannel<Ch, R>[] = [];
  for (const c of channels) {
    if (isHiddenByMe(hidden, c.channel)) continue;
    const regions = c.regions.filter(
      (r) => !isHiddenByMe(hidden, c.channel, r.region),
    );
    if (regions.length === 0) continue;
    out.push({ channel: c.channel, regions });
  }
  return out;
}

/** Всё дерево товар → канал → регион после личного скрытия. Товар исчезает, когда не осталось
 *  ни одного видимого канала. Листья возвращаются ТЕМИ ЖЕ объектами: скрытие — фильтр обзора,
 *  числа и решения оно не трогает. */
export function visibleProducts<P, Ch extends string, R extends { region: string }>(
  hidden: HiddenByMe,
  products: readonly P[],
  channelsOf: (p: P) => readonly { channel: Ch; regions: readonly R[] }[],
): { product: P; channels: VisibleChannel<Ch, R>[] }[] {
  const out: { product: P; channels: VisibleChannel<Ch, R>[] }[] = [];
  for (const p of products) {
    const channels = visibleChannels(hidden, channelsOf(p));
    if (channels.length === 0) continue;
    out.push({ product: p, channels });
  }
  return out;
}

/** Кодек для `usePersistedState`. Формат разъехался или в хранилище чужой мусор — возвращаем
 *  `undefined` («настройки нет»), а не чиним по кусочкам: половина восстановленного фильтра
 *  хуже честного «ничего не скрыто». */
export function parseHidden(raw: string): HiddenByMe | undefined {
  const v: unknown = JSON.parse(raw);
  if (typeof v !== "object" || v === null) return undefined;
  const { channels, regions } = v as { channels?: unknown; regions?: unknown };
  const strings = (x: unknown): x is string[] =>
    Array.isArray(x) && x.every((s) => typeof s === "string");
  if (!strings(channels) || !strings(regions)) return undefined;
  return { channels: new Set(channels), regions: new Set(regions) };
}

export const stringifyHidden = (h: HiddenByMe): string =>
  JSON.stringify({ channels: [...h.channels], regions: [...h.regions] });

/** Что уедет по кнопке фиксации и что при этом остаётся у человека на экране.
 *
 *  ⚠️ Фиксация идёт по ПОКАЗАННОМУ — включая личное скрытие: строки скрытых каналов и регионов
 *  выпадают из `visibleProducts` ещё до сборки «Решений», и в поставки они не попадают. Долго
 *  считалось (и было написано в подсказках) обратное — «фиксация «всех» скрытое видит», — а
 *  разница вскрывалась молча: спрятанный регион при тех же товарах не менял ни счётчик на кнопке,
 *  ни подтверждение «ВСЕ товары», и решения по нему просто не уезжали. Поэтому и счёт, и слова
 *  собираются здесь, в одном месте: у кнопки, подсказки и `confirm` не должно быть трёх правд. */
export interface FixationScope {
  /** Уедет НЕ всё: таблицу сузили фильтры или что-то скрыто у себя. */
  partial: boolean;
  /** Скрытое у себя — в тех же числах, что и счётчик над таблицей. */
  hidden: { channels: number; regions: number; total: number };
  /** Готовая фраза о скрытом или пустая строка. Строчная и без точки — её вставляют в середину
   *  подсказки и подтверждения. */
  hiddenNote: string;
}

/** Объём фиксации по числам экрана. `shown` — товаров в таблице сейчас, `total` — всего до
 *  фильтров, `filters` — сколько фильтров колонок стоит (фильтр по СТРОКАМ может не тронуть ни
 *  одного товара: товаров столько же, а уедет меньше). */
export function fixationScope({
  hidden,
  shown,
  total,
  filters,
}: {
  hidden: HiddenByMe;
  shown: number;
  total: number;
  filters: number;
}): FixationScope {
  const counts = hiddenCounts(hidden);
  return {
    partial: shown < total || filters > 0 || counts.total > 0,
    hidden: counts,
    hiddenNote:
      counts.total > 0 ? `скрыто у вас: ${hiddenWords(hidden)} — НЕ уедут` : "",
  };
}
